import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { localStrings } from "../i18n/homeStrings";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import DatePicker from "../components/DatePicker";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  ChevronLeft, ChevronRight, Search, Download, Loader2, FilterX, ArrowLeft, ClipboardList,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Escape sequences are interpreted in JavaScript strings but printed verbatim
// in JSX text. Built here, where it is a string, rather than typed inline.
const DASH = "\u2014";
const EN_DASH = "\u2013";

export function AuditLog() {
  const { t, language } = useLanguage();
  const s = localStrings(language);

  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  // Filters
  const [search, setSearch] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [actionTypeFilter, setActionTypeFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Filter options
  const [filterUsers, setFilterUsers] = useState([]);
  const [filterActionTypes, setFilterActionTypes] = useState([]);

  const fetchFilters = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/audit-logs/filters`, { withCredentials: true });
      setFilterUsers(data.users || []);
      setFilterActionTypes(data.action_types || []);
    } catch {
      // silent
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: 50 };
      if (search) params.search = search;
      if (userFilter) params.user_email = userFilter;
      if (actionTypeFilter) params.action_type = actionTypeFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const { data } = await axios.get(`${API}/audit-logs`, { params, withCredentials: true });
      setLogs(data.logs);
      setTotal(data.total);
      setTotalPages(data.total_pages);
    } catch {
      toast.error(t("audit.fetchError"));
    } finally {
      setLoading(false);
    }
  }, [page, search, userFilter, actionTypeFilter, dateFrom, dateTo, t]);

  useEffect(() => { fetchFilters(); }, [fetchFilters]);
  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchLogs();
  };

  const clearFilters = () => {
    setSearch("");
    setUserFilter("");
    setActionTypeFilter("");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  const hasFilters = search || userFilter || actionTypeFilter || dateFrom || dateTo;

  const formatTimestamp = (ts) => {
    try {
      const d = new Date(ts);
      return d.toLocaleString(language, {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit", second: "2-digit",
      });
    } catch { return ts; }
  };

  // files is what every upload, download and deletion is written under, and it
  // was missing here: the fallback below prints the key itself, so the busiest
  // type in the log showed the bare English word on an Italian page. sharepoint
  // stays for the entries written before that section was removed.
  const actionTypeLabel = (type) => {
    const labels = {
      auth: t("audit.type.auth"),
      user: t("audit.type.user"),
      password: t("audit.type.password"),
      settings: t("audit.type.settings"),
      security: t("audit.type.security"),
      files: t("audit.type.files"),
      sharepoint: t("audit.type.sharepoint"),
      backup: t("audit.type.backup"),
      project: t("audit.type.project"),
      other: t("audit.type.other"),
    };
    return labels[type] || type;
  };

  const exportPDF = async () => {
    setExporting(true);
    try {
      // Fetch ALL matching entries for the current filters (no pagination)
      const params = { page: 1, per_page: 10000 };
      if (search) params.search = search;
      if (userFilter) params.user_email = userFilter;
      if (actionTypeFilter) params.action_type = actionTypeFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const { data } = await axios.get(`${API}/audit-logs`, { params, withCredentials: true });
      const allLogs = data.logs;

      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      const pageWidth = doc.internal.pageSize.getWidth();

      // Title
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text(`AMMO Core ${DASH} ${t("audit.title")}`, 14, 16);

      // Filters applied
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 100, 100);
      let filterY = 23;
      const filters = [];
      if (search) filters.push(`${t("audit.search")}: "${search}"`);
      if (userFilter) filters.push(`${t("audit.user")}: ${userFilter}`);
      if (actionTypeFilter) filters.push(`${t("audit.actionType")}: ${actionTypeLabel(actionTypeFilter)}`);
      if (dateFrom) filters.push(`${t("audit.from")}: ${dateFrom}`);
      if (dateTo) filters.push(`${t("audit.to")}: ${dateTo}`);
      if (filters.length > 0) {
        doc.text(`${t("audit.appliedFilters")}: ${filters.join(" | ")}`, 14, filterY);
        filterY += 5;
      }
      doc.text(
        `${t("audit.totalEntries")}: ${allLogs.length}  ${DASH}  ${t("audit.exported")}: ${new Date().toLocaleString(language)}`,
        14, filterY
      );
      filterY += 4;

      doc.setDrawColor(200);
      doc.line(14, filterY, pageWidth - 14, filterY);

      // Table
      doc.setTextColor(0, 0, 0);
      autoTable(doc, {
        startY: filterY + 3,
        head: [[t("audit.col.datetime"), t("audit.col.user"), t("audit.col.action"), t("audit.col.ip")]],
        body: allLogs.map((log) => [
          formatTimestamp(log.timestamp),
          log.user_name || log.user_email || DASH,
          log.action || DASH,
          log.ip || DASH,
        ]),
        styles: { fontSize: 8, cellPadding: 2.5 },
        headStyles: { fillColor: [55, 48, 163], textColor: 255, fontStyle: "bold" },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        margin: { left: 14, right: 14 },
        didDrawPage: () => {
          doc.setFontSize(7);
          doc.setTextColor(160);
          doc.text(
            `AMMO Core Audit Log ${DASH} Page ${doc.internal.getCurrentPageInfo().pageNumber}`,
            14, doc.internal.pageSize.getHeight() - 8
          );
        },
      });

      doc.save(`audit_log_${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success(t("audit.exportSuccess"));
    } catch (err) {
      console.error("PDF export error:", err);
      toast.error(t("audit.exportError"));
    } finally {
      setExporting(false);
    }
  };

  const actionTypeBadge = (type) => {
    const colors = {
      auth: "bg-blue-100 text-blue-700",
      user: "bg-violet-100 text-violet-700",
      password: "bg-amber-100 text-amber-700",
      settings: "bg-slate-100 text-slate-700",
      security: "bg-red-100 text-red-700",
      files: "bg-sky-100 text-sky-700",
      sharepoint: "bg-cyan-100 text-cyan-700",
      backup: "bg-emerald-100 text-emerald-700",
      project: "bg-indigo-100 text-indigo-700",
      other: "bg-gray-100 text-gray-600",
    };
    return colors[type] || colors.other;
  };

  return (
    <div>
      <Link to="/" data-testid="back-to-home" className="inline-flex items-center text-sm text-slate-500 hover:text-indigo-700 mb-6 transition-colors">
        <ArrowLeft className="w-4 h-4 mr-1" />
        {t("audit.backToHome")}
      </Link>

      {/* Stacks on a phone: the title and "Esporta PDF" side by side are wider
          than the screen, and the button was the half that left it. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-[#E2B112] flex items-center justify-center shrink-0">
            <ClipboardList className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900" data-testid="audit-log-title">{t("audit.title")}</h2>
        </div>
        <Button
          onClick={exportPDF}
          disabled={exporting || total === 0}
          data-testid="audit-export-pdf-btn"
          className="bg-indigo-700 hover:bg-indigo-600 text-white w-full sm:w-auto"
        >
          {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}
          {t("audit.exportPDF")}
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 shadow-sm" data-testid="audit-filters">
        <form onSubmit={handleSearch} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
          <div className="sm:col-span-2 lg:col-span-1">
            <label className="text-xs font-medium text-slate-500 mb-1 block">{t("audit.search")}</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <Input
                data-testid="audit-search-input"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder={t("audit.searchPlaceholder")}
                className="pl-8 h-10 border-slate-200 text-sm"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">{t("audit.user")}</label>
            <Select value={userFilter} onValueChange={(v) => { setUserFilter(v === "__all__" ? "" : v); setPage(1); }}>
              <SelectTrigger data-testid="audit-filter-user" className="h-10 border-slate-200 text-sm">
                <SelectValue placeholder={t("audit.allUsers")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{t("audit.allUsers")}</SelectItem>
                {filterUsers.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">{t("audit.actionType")}</label>
            <Select value={actionTypeFilter} onValueChange={(v) => { setActionTypeFilter(v === "__all__" ? "" : v); setPage(1); }}>
              <SelectTrigger data-testid="audit-filter-action-type" className="h-10 border-slate-200 text-sm">
                <SelectValue placeholder={t("audit.allTypes")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{t("audit.allTypes")}</SelectItem>
                {filterActionTypes.map((a) => <SelectItem key={a} value={a}>{actionTypeLabel(a)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* The application's own picker rather than the browser's, so these
              two fields match the ones in the decay calculator instead of
              looking like a different application in every browser. */}
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">{t("audit.from")}</label>
            <DatePicker
              testId="audit-filter-date-from"
              value={dateFrom}
              onChange={(v) => { setDateFrom(v); setPage(1); }}
              placeholder={s("date.choose")}
            />
          </div>

          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">{t("audit.to")}</label>
            <DatePicker
              testId="audit-filter-date-to"
              value={dateTo}
              onChange={(v) => { setDateTo(v); setPage(1); }}
              placeholder={s("date.choose")}
            />
          </div>

          {hasFilters && (
            <div className="sm:col-span-2 lg:col-span-5">
              <Button type="button" variant="ghost" size="sm" onClick={clearFilters} data-testid="audit-clear-filters" className="text-slate-500 hover:text-red-600">
                <FilterX className="w-4 h-4 mr-1" />{t("audit.clearFilters")}
              </Button>
            </div>
          )}
        </form>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="audit-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left px-4 py-3 font-semibold text-slate-600 whitespace-nowrap">{t("audit.col.datetime")}</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600 whitespace-nowrap">{t("audit.col.user")}</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600 min-w-[16rem]">{t("audit.col.action")}</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600 whitespace-nowrap">{t("audit.col.type")}</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600 whitespace-nowrap">{t("audit.col.ip")}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="text-center py-12">
                  <Loader2 className="w-5 h-5 animate-spin text-indigo-600 mx-auto" />
                </td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-12 text-slate-400">{t("audit.noEntries")}</td></tr>
              ) : (
                logs.map((log, idx) => (
                  <tr key={idx} data-testid={`audit-row-${idx}`} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap text-slate-500 tabular-nums">{formatTimestamp(log.timestamp)}</td>
                    <td className="px-4 py-3 whitespace-nowrap font-medium text-slate-700">{log.user_name || log.user_email || DASH}</td>
                    <td className="px-4 py-3 text-slate-700">{log.action}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${actionTypeBadge(log.action_type)}`}>
                        {actionTypeLabel(log.action_type)}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-slate-400 font-mono text-xs">{log.ip || DASH}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination. Wraps and centres on a phone: the count and the two
            arrows on one line were wider than the screen, and the arrows were
            the half that went. */}
        {totalPages > 1 && (
          <div className="flex flex-wrap items-center justify-center sm:justify-between gap-3 px-4 py-3 border-t border-slate-200 bg-slate-50" data-testid="audit-pagination">
            <span className="text-sm text-slate-500">
              {`${t("audit.showing")} ${(page - 1) * 50 + 1}${EN_DASH}${Math.min(page * 50, total)} ${t("audit.of")} ${total}`}
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline" size="icon"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
                data-testid="audit-prev-page"
                className="h-9 w-9 border-slate-200"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-slate-600 px-3 tabular-nums">{page} / {totalPages}</span>
              <Button
                variant="outline" size="icon"
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
                data-testid="audit-next-page"
                className="h-9 w-9 border-slate-200"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
